import { useEffect, useRef, useState } from "react";
import { TpvDialog } from "../../components/TpvDialog";
import { handoverPickupOrder } from "../../api/orders";

export const PickupHandover = ({
  order,
  compact = false,
  disabled = false,
  onDelivered,
  onBusyChange = () => {},
  onError = () => {},
}) => {
  const [confirming, setConfirming] = useState(false),
    [legacyConfirmed, setLegacyConfirmed] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const pending = useRef(false),
    trigger = useRef(null),
    confirm = useRef(null);
  const pickup = order.pickup;
  const legacyInput = useRef(null),
    wasConfirming = useRef(false);
  useEffect(() => {
    if (confirming)
      (pickup?.legacy ? legacyInput.current : confirm.current)?.focus();
    else if (wasConfirming.current) trigger.current?.focus();
    wasConfirming.current = confirming;
  }, [confirming, pickup?.legacy]);
  useEffect(() => {
    if (!confirming) return;
    const escape = (event) => {
      if (event.key !== "Escape" || pending.current) return;
      event.preventDefault();
      setConfirming(false);
      setLegacyConfirmed(false);
      trigger.current?.focus();
    };
    document.addEventListener("keydown", escape);
    return () => document.removeEventListener("keydown", escape);
  }, [confirming]);
  if (
    !pickup ||
    pickup.type === "delivery" ||
    [
      "delivered",
      "cancelled",
      "abandoned",
      "inDelivery",
      "returned",
      "notDelivered",
    ].includes(order.status)
  )
    return null;
  const cancel = () => {
    if (pending.current) return;
    setConfirming(false);
    setLegacyConfirmed(false);
    trigger.current?.focus();
  };
  const submit = async () => {
    if (
      pending.current ||
      !pickup.canDeliver ||
      (pickup.legacy && !legacyConfirmed)
    )
      return;
    pending.current = true;
    setBusy(true);
    onBusyChange(true);
    setError("");
    try {
      const response = await handoverPickupOrder(order.orderId, {
        confirmLegacyPickup: pickup.legacy && legacyConfirmed,
      });
      if (
        response?.error !== false ||
        response.data?.orderId !== order.orderId ||
        response.data?.status !== "delivered" ||
        response.data?.paymentStatus !== "paid"
      )
        throw Error(
          (response?.error === true ? response.message : null) ||
            "No se pudo confirmar la entrega. Consulta el pedido antes de volver a intentarlo.",
        );
      onDelivered(response.data);
    } catch (e) {
      const message =
        !e.message || e.message === "Network error"
          ? "No se pudo confirmar la entrega. Consulta el pedido antes de volver a intentarlo."
          : e.message;
      setError(message);
      onError(message);
    } finally {
      pending.current = false;
      setBusy(false);
      onBusyChange(false);
    }
  };
  const panel = (
    <section
      className="pickupHandover"
      aria-label="Entrega en tienda"
      aria-busy={busy}
    >
      <div>
        <strong>Entrega en tienda</strong>
        <p>
          {pickup.legacy
            ? "Pedido anterior sin tipo registrado. Solo confirma si es de recogida en tienda."
            : order.paymentStatus === "paid"
              ? "Confirma la entrega física al cliente. El pago ya registrado se conserva."
              : "Confirma la entrega física al cliente cuando el pedido esté pagado."}
        </p>
        {pickup.legacy && (
          <p>Método registrado: {order.shippingMethod || "Sin identificar"}</p>
        )}
      </div>
      {!pickup.canDeliver && (
        <p>
          {pickup.reason === "unpaid"
            ? "Pendiente de pago. Cobra el pedido antes de entregarlo."
            : pickup.reason === "assigned"
              ? "Este pedido tiene un repartidor asignado. Revisa su reparto."
              : pickup.reason === "archived"
                ? "Este pedido está archivado."
                : "Cocina todavía no ha marcado el pedido como preparado."}
        </p>
      )}
      {!confirming ? (
        <button
          type="button"
          ref={trigger}
          className="customButtonColor2"
          disabled={!pickup.canDeliver}
          onClick={() => {
            setConfirming(true);
            setError("");
          }}
        >
          Entregar pedido
        </button>
      ) : (
        <div role="group" aria-label="Confirmar entrega en tienda">
          <p>¿Has entregado este pedido al cliente?</p>
          {pickup.legacy && (
            <label>
              <input
                type="checkbox"
                ref={legacyInput}
                disabled={busy}
                checked={legacyConfirmed}
                onChange={(e) => setLegacyConfirmed(e.target.checked)}
              />
              Confirmo que es recogida en tienda, no entrega a domicilio.
            </label>
          )}
          <div className="pickupHandoverButtons">
            <button
              type="button"
              ref={confirm}
              className="customButtonColor2"
              disabled={busy || (pickup.legacy && !legacyConfirmed)}
              onClick={submit}
            >
              {busy ? "Confirmando…" : "Sí, confirmar entrega"}
            </button>
            <button type="button" disabled={busy} onClick={cancel}>
              Cancelar
            </button>
          </div>
        </div>
      )}
      {error && <p role="alert">{error}</p>}
    </section>
  );
  return compact ? (
    <>
      <button
        type="button"
        ref={trigger}
        className="tpvPrimary"
        disabled={disabled || !pickup.canDeliver || busy}
        title={
          !pickup.canDeliver
            ? pickup.reason === "unpaid"
              ? "Cobra el pedido antes de entregarlo."
              : "El pedido todavía no está disponible para entregar."
            : undefined
        }
        onClick={() => {
          setConfirming(true);
          setError("");
        }}
      >
        Entregar pedido
      </button>
      {confirming && (
        <TpvDialog
          title="Confirmar entrega en tienda"
          busy={busy}
          onClose={cancel}
        >
          <div className="tpvDialogBody">{panel}</div>
        </TpvDialog>
      )}
    </>
  ) : (
    panel
  );
};
